import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Box,
  Tabs,
  Tab,
  Typography,
  TextField,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Autocomplete,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormGroup,
  Checkbox,
  IconButton,
  Button,
  Stack,
  Grid,
  CircularProgress,
} from '@material-ui/core'
import { useSnackbar } from 'notistack'
import { LoadingButton } from '@material-ui/lab'
import { Icon } from '@iconify/react'
import CloseIcon from '@iconify/icons-eva/close-fill'
import { MobileDatePicker } from '@material-ui/lab'
import { UploadMultiFile } from 'src/components/upload'
import axios from 'axios'
import { REST_API_END_POINT } from 'src/constants/Defaultvalues'
import moment from 'moment'
import { supabase } from '../../supabase/supabaseClient'
import { experimentalStyled as styled } from '@material-ui/core/styles'

const LabelStyle = styled(Typography)(({ theme }) => ({
  ...theme.typography.subtitle2,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1),
}))

export default function DesignFormTabs({ designOrderId, onFormDataChange, savedFormData }) {
  const { enqueueSnackbar } = useSnackbar()
  const [templates, setTemplates] = useState([])
  const [activeTab, setActiveTab] = useState(0)
  const [formData, setFormData] = useState({})
  const [submitForms, setSubmitForms] = useState({})
  const [savedForms, setSavedForms] = useState([]) // Array to store saved forms: [{templateId, submitForm}, ...]
  const [savingForms, setSavingForms] = useState({}) // Track which forms are currently being saved: {templateId: true/false}
  const [loading, setLoading] = useState(true) // Track loading state
  const restoredTabsRef = useRef(new Set()) // Track which tabs have been restored to prevent infinite loops
  const hasInitializedRef = useRef(false) // Track if we've initialized from saved data

  // Fetch templates on mount - using batch API (single call)
  useEffect(() => {
    // Only fetch templates once on mount, or if templates array is empty
    if (templates.length > 0 && hasInitializedRef.current) {
      setLoading(false) // Already loaded, stop loading
      return
    }
    
    setLoading(true) // Start loading
    // Fetch all templates with their form data in one API call
    axios
      .post(`${REST_API_END_POINT}design-form/get-all-templates-with-data`, { module: 1 })
      .then((res) => {
        if (res.data.status === 1) {
          let fetchedTemplates = res.data.data || []
          setTemplates(fetchedTemplates)
          
          // Process templates and set form data
          const formDataMap = {}
          const initialForms = {}
          
          fetchedTemplates.forEach(template => {
            // Extract formData from template (it's nested in the response)
            if (template.formData) {
              formDataMap[template.id] = template.formData
              
              // Initialize submit form - use saved data if available, otherwise initialize empty
              if (template.formData.fields) {
                const hasSavedData = savedFormData && savedFormData[template.id] && Object.keys(savedFormData[template.id]).length > 0
                
                if (hasSavedData) {
                  // Use saved data if it exists (restore from parent state)
                  // Process saved form to restore autocomplete values properly
                  const savedData = savedFormData[template.id]
                  const restoredForm = {}
                  
                  // Process each field in savedData
                  Object.keys(savedData).forEach(key => {
                    const fieldValue = savedData[key]
                    
                    // Handle checkbox groups - they should be arrays directly, not objects with value property
                    if (Array.isArray(fieldValue)) {
                      // It's already an array (checkbox group), use it directly
                      restoredForm[key] = fieldValue
                    } else if (fieldValue && typeof fieldValue === 'object') {
                      // It's an object - check if it's a checkbox group stored incorrectly
                      // Check if it has a value property that's an array (likely a checkbox group)
                      if (Array.isArray(fieldValue.value)) {
                        // Convert from {content: 'checkbox-group', value: [array]} to [array]
                        restoredForm[key] = fieldValue.value
                      } else if (fieldValue.content === 'autocomplete') {
                        // Handle autocomplete fields
                        if (typeof fieldValue.value === 'string' && fieldValue.value !== '') {
                          const fieldConfig = Object.values(template.formData.fields).find(f => f.content === key)
                          if (fieldConfig && fieldConfig.options) {
                            const matchingOption = fieldConfig.options.find(
                              opt => opt.label === fieldValue.value || opt.value === fieldValue.value || 
                                     String(opt.label) === String(fieldValue.value) || String(opt.value) === String(fieldValue.value)
                            )
                            if (matchingOption) {
                              restoredForm[key] = {
                                ...fieldValue,
                                value: matchingOption // Replace string with full option object
                              }
                            } else {
                              restoredForm[key] = fieldValue
                            }
                          } else {
                            restoredForm[key] = fieldValue
                          }
                        } else {
                          restoredForm[key] = fieldValue
                        }
                      } else if (fieldValue.content === 'date') {
                        // Handle date fields - convert string dates to unix timestamp if needed
                        if (typeof fieldValue.value === 'string' && fieldValue.value !== '') {
                          const dateMoment = moment(fieldValue.value)
                          if (dateMoment.isValid()) {
                            restoredForm[key] = {
                              ...fieldValue,
                              value: dateMoment.unix()
                            }
                          } else {
                            restoredForm[key] = fieldValue
                          }
                        } else {
                          restoredForm[key] = fieldValue
                        }
                      } else {
                        // Other field types - keep as is
                        restoredForm[key] = fieldValue
                      }
                    } else {
                      // Primitive value - keep as is
                      restoredForm[key] = fieldValue
                    }
                  })
                  
                  initialForms[template.id] = restoredForm
                } else {
                  // Only initialize empty form if no saved data exists
                  const initialForm = {}
                  Object.entries(template.formData.fields).forEach(([key, value]) => {
                    const { tag, content, field_id, required } = value
                    initialForm[content] = {
                      field_id,
                      content: tag,
                      required,
                      value: '',
                    }
                  })
                  initialForms[template.id] = initialForm
                }
              }
            }
          })
          
          // Set all form data at once
          if (Object.keys(formDataMap).length > 0) {
            setFormData(prev => ({
              ...prev,
              ...formDataMap
            }))
          }
          
          // Set submit forms - either from saved data or initialize empty
          if (Object.keys(initialForms).length > 0) {
            setSubmitForms(prev => ({
              ...prev,
              ...initialForms
            }))
          }
          
          // Restore savedForms from savedFormData if provided (for showing checkmarks)
          if (savedFormData && Object.keys(savedFormData).length > 0) {
            const restoredSavedForms = Object.entries(savedFormData).map(([templateId, submitForm]) => ({
              templateId: parseInt(templateId),
              submitForm
            }))
            setSavedForms(restoredSavedForms)
          }
          
          hasInitializedRef.current = true
        }
        setLoading(false) // Stop loading on success
      })
      .catch((error) => {
        console.log(error)
        setLoading(false) // Stop loading on error
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Simple sort: templates with saved data come first (only in edit mode)
  useEffect(() => {
    if (designOrderId && templates.length > 0 && savedFormData && Object.keys(savedFormData).length > 0) {
      const sortedTemplates = [...templates].sort((a, b) => {
        const aHasData = savedFormData[a.id] && Object.keys(savedFormData[a.id]).length > 0
        const bHasData = savedFormData[b.id] && Object.keys(savedFormData[b.id]).length > 0
        if (aHasData && !bHasData) return -1
        if (!aHasData && bHasData) return 1
        return 0
      })
      setTemplates(sortedTemplates)
    }
  }, [designOrderId, templates.length, savedFormData])

  // Keep ref of savedForms for access without causing dependency issues
  const savedFormsRef = useRef(savedForms)
  savedFormsRef.current = savedForms

  // Effect to populate forms when savedFormData becomes available (after initial mount)
  useEffect(() => {
    // Only run if we have templates loaded and savedFormData is available
    if (templates.length === 0 || !savedFormData || Object.keys(savedFormData).length === 0) {
      return
    }

    // Check if we need to populate forms with saved data
    // Only update if forms are empty or don't match saved data
    const needsUpdate = templates.some(template => {
      const templateId = template.id
      const hasSavedData = savedFormData[templateId] && Object.keys(savedFormData[templateId]).length > 0
      if (!hasSavedData) return false
      
      const currentForm = submitForms[templateId]
      const currentFormEmpty = !currentForm || Object.keys(currentForm).length === 0
      
      // Also check if saved data is different from current form
      if (!currentFormEmpty) {
        // Compare saved data with current form
        const savedData = savedFormData[templateId]
        const hasDifferences = Object.keys(savedData).some(key => {
          const savedField = savedData[key]
          const currentField = currentForm[key]
          
          // Handle checkbox groups - they're stored as arrays
          // Check if savedField is an object with value array (from ViewDesignOrder conversion)
          let savedArray = savedField
          if (savedField && typeof savedField === 'object' && !Array.isArray(savedField) && Array.isArray(savedField.value)) {
            savedArray = savedField.value
          }
          
          if (Array.isArray(savedArray)) {
            // Extract current array if it's wrapped in an object
            let currentArray = currentField
            if (currentField && typeof currentField === 'object' && !Array.isArray(currentField) && Array.isArray(currentField.value)) {
              currentArray = currentField.value
            }
            if (!Array.isArray(currentArray)) {
              currentArray = []
            }
            
            // If current form has checkbox array and saved data also has checkbox array,
            // be conservative - only update if arrays are significantly different
            // This prevents overwriting checkboxes that were just saved
            if (currentArray.length > 0 && savedArray.length > 0) {
              // Both have data - only update if they're truly different
              // Sort arrays for comparison to handle order differences
              const currentSorted = JSON.stringify([...currentArray].sort((a, b) => {
                const aKey = `${a?.field_id}_${a?.value}`
                const bKey = `${b?.field_id}_${b?.value}`
                return aKey.localeCompare(bKey)
              }))
              const savedSorted = JSON.stringify([...savedArray].sort((a, b) => {
                const aKey = `${a?.field_id}_${a?.value}`
                const bKey = `${b?.field_id}_${b?.value}`
                return aKey.localeCompare(bKey)
              }))
              return currentSorted !== savedSorted
            }
            
            // If one is empty and other has data, update
            if (savedArray.length !== currentArray.length) return true
            // Deep comparison for arrays
            return JSON.stringify(savedArray) !== JSON.stringify(currentArray)
          }
          
          // Handle objects with value property
          const savedValue = savedField?.value !== undefined ? savedField.value : savedField
          const currentValue = currentField?.value !== undefined ? currentField.value : currentField
          
          // Skip if both are empty
          if (!savedValue && !currentValue) return false
          
          // Deep comparison for objects/arrays
          if (typeof savedValue === 'object' && typeof currentValue === 'object') {
            return JSON.stringify(savedValue) !== JSON.stringify(currentValue)
          }
          return savedValue !== currentValue
        })
        
        // Only update if there are meaningful differences
        return hasDifferences
      }
      
      return currentFormEmpty
    })

    if (!needsUpdate) return

    // Populate forms with saved data
    const updatedForms = { ...submitForms }
    const updatedSavedForms = []

    templates.forEach(template => {
      const templateId = template.id
      const savedData = savedFormData[templateId]

      if (savedData && Object.keys(savedData).length > 0) {
        // Get template data to help restore autocomplete values
        const templateData = formData[templateId]
        
        // Process saved form to restore autocomplete values properly
        const restoredForm = {}
        
        // Process each field in savedData
        Object.keys(savedData).forEach(key => {
          const fieldValue = savedData[key]
          
          // Handle checkbox groups - they should be arrays directly, not objects with value property
          if (Array.isArray(fieldValue)) {
            // It's already an array (checkbox group), use it directly
            restoredForm[key] = fieldValue
          } else if (fieldValue && typeof fieldValue === 'object') {
            // It's an object - check if it's a checkbox group stored incorrectly
            // Check if it has a value property that's an array (likely a checkbox group)
            if (Array.isArray(fieldValue.value)) {
              // Convert from {content: 'checkbox-group', value: [array]} to [array]
              // This happens when data comes from ViewDesignOrder.js conversion
              // The structure is: {field_id, content, value: [array], required}
              // We need to extract just the array: [array]
              restoredForm[key] = fieldValue.value
            } else if (fieldValue.content === 'autocomplete') {
              // Handle autocomplete fields
              if (typeof fieldValue.value === 'string' && fieldValue.value !== '') {
                // If we have template data, try to find the matching option object
                if (templateData && templateData.fields) {
                  const fieldConfig = Object.values(templateData.fields).find(f => f.content === key)
                  if (fieldConfig && fieldConfig.options) {
                    const matchingOption = fieldConfig.options.find(
                      opt => opt.label === fieldValue.value || opt.value === fieldValue.value || 
                             String(opt.label) === String(fieldValue.value) || String(opt.value) === String(fieldValue.value)
                    )
                    if (matchingOption) {
                      restoredForm[key] = {
                        ...fieldValue,
                        value: matchingOption // Replace string with full option object
                      }
                    } else {
                      restoredForm[key] = fieldValue
                    }
                  } else {
                    restoredForm[key] = fieldValue
                  }
                } else {
                  restoredForm[key] = fieldValue
                }
              } else {
                restoredForm[key] = fieldValue
              }
            } else if (fieldValue.content === 'date') {
              // Handle date fields - convert string dates to unix timestamp if needed
              if (typeof fieldValue.value === 'string' && fieldValue.value !== '') {
                const dateMoment = moment(fieldValue.value)
                if (dateMoment.isValid()) {
                  restoredForm[key] = {
                    ...fieldValue,
                    value: dateMoment.unix()
                  }
                } else {
                  restoredForm[key] = fieldValue
                }
              } else {
                restoredForm[key] = fieldValue
              }
            } else if (fieldValue.content) {
              // Other field types with content property (text, textarea, select, etc.) - keep structure but ensure value is a string
              // If value is an object, convert it to string or extract the actual value
              if (fieldValue.value && typeof fieldValue.value === 'object' && !Array.isArray(fieldValue.value)) {
                // If value is an object, try to extract a string value or use empty string
                const stringValue = fieldValue.value?.label || fieldValue.value?.value || fieldValue.value?.toString() || ''
                restoredForm[key] = {
                  ...fieldValue,
                  value: stringValue
                }
              } else {
                // Keep as is for primitive values
                restoredForm[key] = fieldValue
              }
            } else {
              // Unknown format - try to preserve structure
              restoredForm[key] = fieldValue
            }
          } else {
            // Primitive value - keep as is
            restoredForm[key] = fieldValue
          }
        })

        updatedForms[templateId] = restoredForm
        updatedSavedForms.push({
          templateId: parseInt(templateId),
          submitForm: restoredForm
        })
      }
    })

    // Only update if we have changes
    if (Object.keys(updatedForms).length > 0) {
      setSubmitForms(updatedForms)
    }
    
    if (updatedSavedForms.length > 0) {
      setSavedForms(updatedSavedForms)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFormData, templates, formData])

  // Restore saved form data when switching tabs - with proper checks to prevent infinite loops
  // CRITICAL: Don't overwrite existing form data when switching tabs - preserve checkbox arrays
  useEffect(() => {
    if (templates.length > 0 && activeTab < templates.length) {
      const currentTemplateId = templates[activeTab]?.id
      if (!currentTemplateId) return
      
      setSubmitForms(prev => {
        const currentForm = prev[currentTemplateId]
        
        // CRITICAL: If form already exists in submitForms, DON'T overwrite it
        // This preserves checkbox arrays and other form data when switching tabs
        // Only restore from savedForms if the form is completely empty
        if (currentForm && Object.keys(currentForm).length > 0) {
          // Form already exists - preserve it (especially checkbox arrays)
          return prev
        }
        
        // Only restore if form is empty - use savedFormData (from parent) or savedForms
        // First try savedFormData (from parent - has checkbox arrays in correct format)
        if (savedFormData && savedFormData[currentTemplateId] && Object.keys(savedFormData[currentTemplateId]).length > 0) {
          const savedData = savedFormData[currentTemplateId]
          const templateData = formData[currentTemplateId]
          
          // Process saved form to restore autocomplete values properly
          const restoredForm = {}
          
          Object.keys(savedData).forEach(key => {
            const fieldValue = savedData[key]
            
            // Handle checkbox groups - they should be arrays directly
            if (Array.isArray(fieldValue)) {
              restoredForm[key] = fieldValue
            } else if (fieldValue && typeof fieldValue === 'object') {
              // Check if it's a checkbox group stored as object with value array
              if (Array.isArray(fieldValue.value)) {
                restoredForm[key] = fieldValue.value
              } else if (fieldValue.content === 'autocomplete') {
                // Handle autocomplete fields
                if (typeof fieldValue.value === 'string' && fieldValue.value !== '') {
                  if (templateData && templateData.fields) {
                    const fieldConfig = Object.values(templateData.fields).find(f => f.content === key)
                    if (fieldConfig && fieldConfig.options) {
                      const matchingOption = fieldConfig.options.find(
                        opt => opt.label === fieldValue.value || opt.value === fieldValue.value
                      )
                      if (matchingOption) {
                        restoredForm[key] = {
                          ...fieldValue,
                          value: matchingOption
                        }
                      } else {
                        restoredForm[key] = fieldValue
                      }
                    } else {
                      restoredForm[key] = fieldValue
                    }
                  } else {
                    restoredForm[key] = fieldValue
                  }
                } else {
                  restoredForm[key] = fieldValue
                }
              } else {
                // Other field types - keep as is
                restoredForm[key] = fieldValue
              }
            } else {
              restoredForm[key] = fieldValue
            }
          })
          
          return {
            ...prev,
            [currentTemplateId]: restoredForm
          }
        }
        
        // Fallback to savedForms if savedFormData doesn't have it
        const savedForm = savedFormsRef.current.find(f => f.templateId === currentTemplateId)
        if (savedForm && savedForm.submitForm) {
          const templateData = formData[currentTemplateId]
          const restoredForm = { ...savedForm.submitForm }
          
          // Fix autocomplete values if needed
          if (templateData && templateData.fields) {
            Object.keys(restoredForm).forEach(key => {
              const fieldValue = restoredForm[key]
              if (fieldValue && typeof fieldValue === 'object' && fieldValue.content === 'autocomplete') {
                if (typeof fieldValue.value === 'string') {
                  const fieldConfig = Object.values(templateData.fields).find(f => f.content === key)
                  if (fieldConfig && fieldConfig.options) {
                    const matchingOption = fieldConfig.options.find(
                      opt => opt.label === fieldValue.value || opt.value === fieldValue.value
                    )
                    if (matchingOption) {
                      restoredForm[key] = {
                        ...fieldValue,
                        value: matchingOption
                      }
                    }
                  }
                }
              }
            })
          }
          
          return {
            ...prev,
            [currentTemplateId]: restoredForm
          }
        }
        
        return prev
      })
    }
    // Only depend on activeTab and templates, not savedForms to prevent loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, templates.length, savedFormData])

  const fetchTemplateData = useCallback(async (templateId) => {
    try {
      const res = await axios.post(`${REST_API_END_POINT}design-form/get-template-by-id`, { id: templateId })
      if (res.data && res.data.data && res.data.data[0]) {
        setFormData(prev => ({
          ...prev,
          [templateId]: res.data.data[0]
        }))
        
        // Initialize empty form - don't check savedForms here to avoid dependency issues
        const template = res.data.data[0]
        if (template.fields) {
          const initialForm = {}
          Object.entries(template.fields).forEach(([key, value]) => {
            const { tag, content, field_id, required } = value
            initialForm[content] = {
              field_id,
              content: tag,
              required,
              value: '',
            }
          })
          setSubmitForms(prev => {
            // Only initialize if form doesn't exist yet
            if (prev[templateId]) {
              return prev
            }
            return {
              ...prev,
              [templateId]: initialForm
            }
          })
        }
      }
    } catch (error) {
      console.log(error)
    }
  }, [])

  const handleDropFile = useCallback(
    async (acceptedFiles, field_id, content, templateId) => {
      try {
        if (!acceptedFiles?.length) return

        const uploadedFiles = []

        for (const file of acceptedFiles) {
          const ts = Date.now()
          const cleanName = file.name.replace(/\s+/g, '_')
          const fileName = `${ts}_${cleanName}`
          const filePath = `design-orders/design-form/attachments/${fileName}`

          const { error: upErr } = await supabase
            .storage
            .from('signwise-attachments')
            .upload(filePath, file)

          if (upErr) {
            console.error('Upload error:', upErr.message)
            continue
          }

          const { data: urlData, error: urlErr } = supabase
            .storage
            .from('signwise-attachments')
            .getPublicUrl(filePath)

          if (urlErr || !urlData?.publicUrl) {
            console.error(urlErr?.message || 'Could not get URL')
            continue
          }

          uploadedFiles.push({
            field_id,
            value: urlData.publicUrl,
            content: 'file',
            preview: urlData.publicUrl,
            name: file.name,
            url: urlData.publicUrl
          })
        }

        if (!uploadedFiles.length) return

        setSubmitForms(prev => {
          const newForm = { ...prev[templateId] || {} }
          const existing = Object.keys(newForm).filter(k => k.startsWith(content))
          const lastIndex = existing.length
            ? Math.max(...existing.map(k => parseInt(k.split('_').pop() || '0', 10)))
            : 0

          uploadedFiles.forEach((file, i) => {
            newForm[`${content}_${lastIndex + i + 1}`] = file
          })

          return {
            ...prev,
            [templateId]: newForm
          }
        })
      } catch (err) {
        console.error('Unexpected error:', err)
      }
    },
    []
  )

  const ActivityComponent = ({ activity }) => {
    return (
      <Typography
        variant="body1"
        component="div"
        sx={{ '& strong': { fontWeight: 'bold' } }}
        dangerouslySetInnerHTML={{ __html: activity }}
      />
    )
  }

  // Helper to render HTML content
  const renderContent = (htmlContent) => {
    if (!htmlContent) return ''
    return (
      <div
        style={{ whiteSpace: 'pre-wrap' }}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    )
  }

  // Common field wrapper style
  const fieldWrapperStyle = {
    marginBottom: '16px',
  }

  // Label style with asterisk icon
  const labelStyle = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#333',
  }

  const asteriskStyle = {
    color: '#d32f2f',
    marginRight: '4px',
    fontSize: '16px',
  }

  // Helper function to safely extract string value from field data
  const getFieldValue = (fieldData) => {
    if (!fieldData) return ''
    if (typeof fieldData === 'string' || typeof fieldData === 'number') return String(fieldData)
    if (Array.isArray(fieldData)) return '' // Arrays are for checkbox groups, not text fields
    if (typeof fieldData === 'object') {
      // If it's an object with a value property
      if (fieldData.value !== undefined) {
        if (typeof fieldData.value === 'string' || typeof fieldData.value === 'number') {
          return String(fieldData.value)
        }
        if (typeof fieldData.value === 'object' && !Array.isArray(fieldData.value)) {
          // Try to extract label or value from nested object
          return fieldData.value?.label || fieldData.value?.value || String(fieldData.value) || ''
        }
        if (Array.isArray(fieldData.value)) {
          return '' // Arrays are for checkbox groups
        }
        return String(fieldData.value) || ''
      }
      // If it's an object without value property, try to extract something
      return fieldData?.label || fieldData?.value || String(fieldData) || ''
    }
    return String(fieldData) || ''
  }

  const renderField = (key, value, templateId) => {
    const { tag, config, content, field_id, required, options } = value
    const submitForm = submitForms[templateId] || {}

    switch (tag) {
      case 'paragraph':
        return (
          <Typography
            key={value.id || key}
            variant="body2"
            sx={{
              marginBottom: '12px',
              color: '#666',
              lineHeight: 1.6,
            }}
          >
            {renderContent(content)}
          </Typography>
        )
      case 'header':
        return (
          <Typography
            key={value.id || key}
            variant="body1"
            sx={{
              color: '#333',
              fontWeight: 600,
              margin: '0 0 5px 0',
              fontSize: '14px',
            }}
          >
            {renderContent(content)}
          </Typography>
        )
      case 'textarea':
        return (
          <Box key={value.id || key} sx={fieldWrapperStyle}>
            <Box sx={labelStyle}>
              {required == 1 && <span style={asteriskStyle}>*</span>}
              {renderContent(content)}
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={getFieldValue(submitForm[content])}
              onChange={(event) =>
                setSubmitForms(prev => ({
                  ...prev,
                  [templateId]: {
                    ...prev[templateId],
                    [content]: {
                      required: required,
                      field_id,
                      content: 'textarea',
                      value: event.target.value,
                    },
                  }
                }))
              }
              multiline
              minRows={3}
              maxRows={7}
              required={required == 1}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  color: '#000',
                },
                '& .MuiInputBase-input': {
                  color: '#000',
                },
                '& .MuiInputLabel-root': {
                  color: '#333',
                },
              }}
            />
          </Box>
        )
      case 'text':
        return (
          <Box key={value.id || key} sx={fieldWrapperStyle}>
            <Box sx={labelStyle}>
              {required == 1 && <span style={asteriskStyle}>*</span>}
              {renderContent(content)}
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={getFieldValue(submitForm[content])}
              onChange={(event) =>
                setSubmitForms(prev => ({
                  ...prev,
                  [templateId]: {
                    ...prev[templateId],
                    [content]: {
                      field_id,
                      content: 'text',
                      required: required,
                      value: event.target.value,
                    },
                  }
                }))
              }
              required={required == 1}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  color: '#000',
                },
                '& .MuiInputBase-input': {
                  color: '#000',
                },
                '& .MuiInputLabel-root': {
                  color: '#333',
                },
              }}
            />
          </Box>
        )
      case 'date':
        return (
          <Box key={value.id || key} sx={fieldWrapperStyle}>
            <Box sx={labelStyle}>
              {required == 1 && <span style={asteriskStyle}>*</span>}
              {renderContent(content)}
            </Box>
            <Box component={MobileDatePicker}
              value={submitForm[content]?.value ? moment.unix(submitForm[content].value) : null}
              onChange={(date) =>
                setSubmitForms(prev => ({
                  ...prev,
                  [templateId]: {
                    ...prev[templateId],
                    [content]: {
                      field_id,
                      content: 'date',
                      required: required,
                      value: moment(date).unix(),
                    },
                  }
                }))
              }
              minDate={moment()}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  variant="outlined"
                  size="small"
                  required={required == 1}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff',
                      color: '#000',
                    },
                    '& .MuiInputBase-input': {
                      color: '#000',
                    },
                    '& .MuiInputLabel-root': {
                      color: '#333',
                    },
                  }}
                />
              )}
            />
          </Box>
        )
      case 'number':
        return (
          <Box key={value.id || key} sx={fieldWrapperStyle}>
            <Box sx={labelStyle}>
              {required == 1 && <span style={asteriskStyle}>*</span>}
              {renderContent(content)}
            </Box>
            <TextField
              fullWidth
              type="number"
              variant="outlined"
              size="small"
              value={getFieldValue(submitForm[content])}
              onChange={(event) =>
                setSubmitForms(prev => ({
                  ...prev,
                  [templateId]: {
                    ...prev[templateId],
                    [content]: {
                      field_id,
                      content: 'number',
                      required: required,
                      value: event.target.value,
                    },
                  }
                }))
              }
              required={required == 1}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  color: '#000',
                },
                '& .MuiInputBase-input': {
                  color: '#000',
                },
                '& .MuiInputLabel-root': {
                  color: '#333',
                },
              }}
            />
          </Box>
        )
      case 'select':
        return (
          <Box key={value.id || key} sx={fieldWrapperStyle}>
            <Box sx={labelStyle}>
              {required == 1 && <span style={asteriskStyle}>*</span>}
              {renderContent(content)}
            </Box>
            <FormControl fullWidth size="small">
              <Select
                variant="outlined"
                displayEmpty
                value={getFieldValue(submitForm[content])}
                onChange={(event) =>
                  setSubmitForms(prev => ({
                    ...prev,
                    [templateId]: {
                      ...prev[templateId],
                      [content]: {
                        field_id,
                        content: 'select',
                        value: event.target.value,
                      },
                    }
                  }))
                }
                sx={{
                  backgroundColor: '#fff',
                  color: '#000',
                  '& .MuiSelect-select': {
                    color: '#000',
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: '#fff',
                      color: '#000',
                      '& .MuiMenuItem-root': {
                        backgroundColor: '#fff',
                        color: '#000',
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                          color: '#000',
                        },
                        '&.Mui-selected': {
                          backgroundColor: '#e3f2fd',
                          color: '#1976d2',
                          '&:hover': {
                            backgroundColor: '#bbdefb',
                          },
                        },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="" disabled>
                  Select an option
                </MenuItem>
                {value?.options?.map((data) => (
                  <MenuItem key={data.value} value={data.value}>
                    {data.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )
      case 'file':
        return (
          <Box 
            key={value.id || key} 
            sx={{
              ...fieldWrapperStyle,
              width: '100%',
              marginBottom: '24px',
            }}
          >
            <Box sx={labelStyle}>
              {required == 1 && <span style={asteriskStyle}>*</span>}
              {renderContent(content)}
            </Box>
            <Box 
              sx={{ 
                marginTop: '12px',
                width: '100%',
                '& h5, & .MuiTypography-h5': {
                  color: '#333 !important',
                },
                '& p, & .MuiTypography-body2': {
                  color: '#666 !important',
                },
                '& span, & .MuiTypography-root': {
                  color: '#666 !important',
                },
              }}
            >
              <UploadMultiFile
                maxSize={1024 * 1024 * 1024}
                accept="image/*"
                showPreview={true}
                files={Object.keys(submitForm)
                  .filter((key) => key.startsWith(content))
                  .map((key) => {
                    const fileData = submitForm[key]
                    // Ensure we have the file URL for preview - match install report format
                    const fileUrl = fileData?.value || fileData?.url || fileData?.preview
                    return {
                      url: fileUrl,
                      name: fileData?.name || `Uploaded Image ${key}`,
                      preview: fileUrl
                    }
                  })
                  .filter(file => file.url) // Only include files with valid URLs
                }
                onDrop={(acceptedFiles) =>
                  handleDropFile(acceptedFiles, field_id, content, templateId)
                }
                onRemove={(fileToRemove) => {
                  setSubmitForms(prev => {
                    const newForm = { ...prev[templateId] || {} }
                    Object.keys(newForm).forEach((key) => {
                      const fileData = newForm[key]
                      if (fileData?.value === fileToRemove.url || fileData?.url === fileToRemove.url || fileData?.preview === fileToRemove.url) {
                        delete newForm[key]
                      }
                    })
                    return {
                      ...prev,
                      [templateId]: newForm
                    }
                  })
                }}
                onRemoveAll={() => {
                  setSubmitForms(prev => {
                    const newForm = { ...prev[templateId] || {} }
                    Object.keys(newForm)
                      .filter((key) => key.startsWith(content))
                      .forEach((key) => {
                        delete newForm[key]
                      })
                    return {
                      ...prev,
                      [templateId]: newForm
                    }
                  })
                }}
              />
            </Box>
          </Box>
        )
      case 'autocomplete':
        return (
          <Box key={value.id || key} sx={fieldWrapperStyle}>
            <Box sx={labelStyle}>
              {required == 1 && <span style={asteriskStyle}>*</span>}
              {renderContent(content)}
            </Box>
            <Autocomplete
              fullWidth
              size="small"
              options={value?.options || []}
              value={
                submitForm[content]?.value 
                  ? (typeof submitForm[content].value === 'string'
                      ? value?.options?.find(opt => opt.label === submitForm[content].value || opt.value === submitForm[content].value) || submitForm[content].value
                      : submitForm[content].value)
                  : null
              }
              onChange={(event, newValue) =>
                setSubmitForms(prev => ({
                  ...prev,
                  [templateId]: {
                    ...prev[templateId],
                    [content]: {
                      value: newValue, // Store the full option object, not just label
                      content: 'autocomplete',
                      field_id,
                    },
                  }
                }))
              }
              isOptionEqualToValue={(option, value) => {
                if (!option || !value) return false
                if (typeof option === 'object' && typeof value === 'object') {
                  return option.label === value.label || option.value === value.value
                }
                if (typeof option === 'object' && typeof value === 'string') {
                  return option.label === value || option.value === value
                }
                if (typeof value === 'object' && typeof option === 'string') {
                  return value.label === option || value.value === option
                }
                return option === value
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select or type..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff',
                      color: '#000',
                    },
                    '& .MuiInputBase-input': {
                      color: '#000',
                    },
                    '& .MuiInputLabel-root': {
                      color: '#333',
                    },
                  }}
                />
              )}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option
                if (typeof option === 'object' && option.label) return option.label
                return ''
              }}
              ListboxProps={{
                sx: {
                  backgroundColor: '#fff',
                  color: '#000',
                  '& .MuiAutocomplete-option': {
                    backgroundColor: '#fff',
                    color: '#000',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      color: '#000',
                    },
                    '&[aria-selected="true"]': {
                      backgroundColor: '#e3f2fd',
                      color: '#1976d2',
                      '&:hover': {
                        backgroundColor: '#bbdefb',
                      },
                    },
                  },
                },
              }}
            />
          </Box>
        )
      case 'radio-group':
        return (
          <Box key={value.id || key} sx={fieldWrapperStyle}>
            <Box sx={labelStyle}>
              {required == 1 && <span style={asteriskStyle}>*</span>}
              {renderContent(content)}
            </Box>
            <RadioGroup
              value={submitForm[content]?.value || ''}
              onChange={(event) =>
                setSubmitForms(prev => ({
                  ...prev,
                  [templateId]: {
                    ...prev[templateId],
                    [content]: {
                      field_id,
                      content: 'radiogroup',
                      value: event?.target?.value,
                    },
                  }
                }))
              }
              sx={{
                marginTop: '8px',
                '& .MuiFormControlLabel-root': {
                  marginLeft: '0px',
                  marginRight: '16px',
                },
                '& .MuiFormControlLabel-label': {
                  color: '#333',
                },
              }}
            >
              {value?.options?.map((data) => (
                <FormControlLabel
                  key={data.value}
                  value={data.label}
                  control={<Radio size="small" />}
                  label={data.label}
                />
              ))}
            </RadioGroup>
          </Box>
        )
      case 'checkbox-group':
        return (
          <Box key={value.id || key} sx={fieldWrapperStyle}>
            <Box sx={labelStyle}>
              {required == 1 && <span style={asteriskStyle}>*</span>}
              {renderContent(content)}
            </Box>
            <FormGroup
              sx={{
                marginTop: '8px',
                '& .MuiFormControlLabel-root': {
                  marginLeft: '0px',
                  marginRight: '16px',
                },
                '& .MuiFormControlLabel-label': {
                  color: '#333',
                },
              }}
            >
              {value?.options?.map((data) => {
                // Check if this checkbox is selected - look in the field-specific content or checkboxgroup
                // Handle both array format and object format {value: [array]}
                let checkboxData = submitForm[content] || submitForm?.checkboxgroup
                
                // If checkboxData is an object with a value property that's an array, extract the array
                if (checkboxData && typeof checkboxData === 'object' && !Array.isArray(checkboxData) && Array.isArray(checkboxData.value)) {
                  checkboxData = checkboxData.value
                }
                
                const isChecked = Array.isArray(checkboxData)
                  ? checkboxData.some((item) => {
                      // Handle both object format {label, value, field_id} and direct value format
                      if (item && typeof item === 'object') {
                        // Compare with option's label and value
                        return (
                          item?.label === data?.value || 
                          item?.value === data?.value ||
                          item?.label === data?.label ||
                          item?.value === data?.label ||
                          String(item?.label) === String(data?.value) ||
                          String(item?.value) === String(data?.value) ||
                          String(item?.label) === String(data?.label) ||
                          String(item?.value) === String(data?.label)
                        )
                      } else {
                        // Direct value comparison
                        return (
                          item === data?.value ||
                          item === data?.label ||
                          String(item) === String(data?.value) ||
                          String(item) === String(data?.label)
                        )
                      }
                    })
                  : false

                return (
                  <FormControlLabel
                    key={data.value}
                    control={
                      <Checkbox
                        size="small"
                        checked={isChecked}
                        onChange={(event) => {
                          const isChecked = event.target.checked
                          setSubmitForms(prev => {
                            const currentForm = prev[templateId] || {}
                            // Read from field-specific [content] array first (new format)
                            // Handle both array format and object format {value: [array]}
                            let currentContent = currentForm[content]
                            
                            // If it's an object with a value property that's an array, extract it
                            if (currentContent && typeof currentContent === 'object' && !Array.isArray(currentContent) && Array.isArray(currentContent.value)) {
                              currentContent = currentContent.value
                            }
                            
                            // Fallback to checkboxgroup for backward compatibility with old data
                            if (!Array.isArray(currentContent)) {
                              currentContent = Array.isArray(currentForm?.checkboxgroup)
                                ? currentForm.checkboxgroup
                                : []
                            }

                            if (isChecked) {
                              // Check if already exists to avoid duplicates
                              const exists = currentContent.some(
                                (item) => item?.label === data?.value || item?.value === data?.value
                              )
                              if (exists) {
                                return prev
                              }

                              return {
                                ...prev,
                                [templateId]: {
                                  ...currentForm,
                                  [content]: [
                                    ...currentContent,
                                    {
                                      field_id,
                                      label: data?.value,
                                      value: data?.label,
                                    },
                                  ],
                                  // Remove checkboxgroup to prevent duplicate saves
                                  // Checkbox values are stored in field-specific [content] array only
                                }
                              }
                            } else {
                              return {
                                ...prev,
                                [templateId]: {
                                  ...currentForm,
                                  [content]: currentContent.filter(
                                    (item) => 
                                      item?.label !== data?.value && 
                                      item?.value !== data?.value
                                  ),
                                  // Remove checkboxgroup to prevent duplicate saves
                                }
                              }
                            }
                          })
                        }}
                      />
                    }
                    label={data.label}
                  />
                )
              })}
            </FormGroup>
          </Box>
        )
      default:
        return null
    }
  }

  // Normalize form data - convert autocomplete objects to strings for backend compatibility
  const normalizeFormData = (formData) => {
    const normalized = { ...formData }
    Object.keys(normalized).forEach(key => {
      const field = normalized[key]
      
      // Remove checkboxgroup key to prevent duplicate saves (checkbox values are stored in field-specific arrays)
      if (key === 'checkboxgroup') {
        delete normalized[key]
        return
      }
      
      // For checkbox groups (arrays), remove duplicates based on field_id and value
      if (Array.isArray(field) && field.length > 0) {
        const seen = new Set()
        const uniqueArray = field.filter(item => {
          if (!item || typeof item !== 'object') return false
          const key = `${item.field_id}_${item.value}`
          if (seen.has(key)) {
            return false // Duplicate, filter it out
          }
          seen.add(key)
          return true
        })
        normalized[key] = uniqueArray
        return // Skip further processing for checkbox arrays
      }
      
      // Handle object fields (text, textarea, number, select, autocomplete, date, etc.)
      if (field && typeof field === 'object' && !Array.isArray(field)) {
        if (field.content === 'autocomplete') {
          // Convert autocomplete object to string (label) for backend
          if (typeof field.value === 'object' && field.value !== null) {
            normalized[key] = {
              ...field,
              value: field.value.label || field.value.value || ''
            }
          }
        } else if (field.value !== undefined) {
          // For other field types, ensure value is a string (not an object)
          // This prevents [object Object] from being saved
          if (typeof field.value === 'object' && field.value !== null && !Array.isArray(field.value)) {
            // If value is an object, try to extract a string value
            const stringValue = field.value?.label || field.value?.value || String(field.value) || ''
            normalized[key] = {
              ...field,
              value: stringValue
            }
          } else if (field.value !== null && field.value !== undefined) {
            // Ensure value is converted to string for text/textarea/number/select fields
            normalized[key] = {
              ...field,
              value: String(field.value)
            }
          }
        }
      }
    })
    return normalized
  }

  // Handle save form button click
  const handleSaveForm = (templateId) => {
    const currentForm = submitForms[templateId]
    if (!currentForm) return

    // Set loading state
    setSavingForms(prev => ({ ...prev, [templateId]: true }))

    // Normalize the form data before saving (convert autocomplete objects to strings)
    const normalizedForm = normalizeFormData(currentForm)

    // Check if form already exists in savedForms
    const existingIndex = savedForms.findIndex(f => f.templateId === templateId)
    
    let updatedSavedForms
    const isUpdate = existingIndex >= 0
    
    if (isUpdate) {
      // Update existing saved form
      updatedSavedForms = [...savedForms]
      updatedSavedForms[existingIndex] = { templateId, submitForm: { ...normalizedForm } }
    } else {
      // Add new saved form
      updatedSavedForms = [...savedForms, { templateId, submitForm: { ...normalizedForm } }]
    }
    
    setSavedForms(updatedSavedForms)
    
    // CRITICAL: Preserve checkbox arrays in submitForms state after saving
    // The normalized form is used for saving, but we keep the original format for display
    // This ensures checkboxes remain checked after clicking "Update Form"
    setSubmitForms(prev => ({
      ...prev,
      [templateId]: { ...currentForm } // Keep original form with checkbox arrays intact
    }))
    
    // Notify parent immediately when form is saved/updated
    // IMPORTANT: Pass the ORIGINAL form data (with checkbox arrays), not normalized
    // The parent will normalize it when saving to backend (in handleConfirmSubmit)
    // This prevents checkbox arrays from being lost and checkboxes from being unchecked
    if (onFormDataChange) {
      const savedFormsObject = {}
      // Build the object from all saved forms, using original data from submitForms
      // This ensures checkbox arrays are preserved
      Object.keys(submitForms).forEach(tid => {
        // Always use submitForms (which has checkbox arrays) instead of normalized savedForms
        if (submitForms[tid]) {
          savedFormsObject[tid] = submitForms[tid] // Use original form with checkbox arrays
        }
      })
      onFormDataChange(savedFormsObject)
    }

    // Show success message
    enqueueSnackbar(
      isUpdate ? 'Form updated successfully' : 'Form saved successfully',
      { variant: 'success' }
    )

    // Clear loading state after a short delay
    setTimeout(() => {
      setSavingForms(prev => ({ ...prev, [templateId]: false }))
    }, 500)
  }

  // Check if form is saved
  const isFormSaved = (templateId) => {
    return savedForms.some(f => f.templateId === templateId)
  }

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading design forms...
        </Typography>
      </Box>
    )
  }

  if (templates.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="body2" color="text.secondary">
          No design forms available
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Tabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        scrollButtons="auto"
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
      >
        {templates.map((template, index) => (
          <Tab
            key={template.id}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>{template.name}</span>
                {isFormSaved(template.id) && (
                  <Icon
                    icon="eva:checkmark-circle-2-fill"
                    width={16}
                    style={{ color: '#4caf50' }}
                  />
                )}
              </Box>
            }
          />
        ))}
      </Tabs>

      {templates.map((template, index) => {
        if (index !== activeTab) return null
        
        const templateData = formData[template.id]
        if (!templateData || !templateData.fields) {
          return (
            <Box key={template.id} sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Loading form...
              </Typography>
            </Box>
          )
        }

        // Convert fields to array and separate file fields
        const fieldsArray = Object.entries(templateData.fields).map(([key, value]) => ({
          key,
          ...value,
        }))
        
        const fileFields = fieldsArray.filter(field => field.tag === 'file')
        const otherFields = fieldsArray.filter(field => field.tag !== 'file')
        
        // Group non-file fields into two columns
        const midPoint = Math.ceil(otherFields.length / 2)
        const leftColumn = otherFields.slice(0, midPoint)
        const rightColumn = otherFields.slice(midPoint)

        return (
          <Box 
            key={template.id}
            sx={{
              backgroundColor: '#fff',
              position: 'relative',
              paddingBottom: '20px',
              borderRadius: '15px',
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          >
            {/* Red Header Banner */}
            <Box
              sx={{
                backgroundColor: '#d32f2f',
                color: '#fff',
                padding: '16px 24px',
                marginBottom: '24px',
                textAlign: 'center',
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: '#fff',
                  fontWeight: 600,
                  margin: 0,
                  textAlign: 'center',
                }}
              >
                {template.name}:
              </Typography>
            </Box>

            {/* Form Content with Two Columns */}
            <Box sx={{ padding: '0 24px 24px 24px' }}>
              <Grid container spacing={3} alignItems="flex-start">
                {/* Left Column */}
                <Grid item xs={12} md={6}>
                  <Stack spacing={2} sx={{ marginTop: 0 }}>
                    {leftColumn.map((field) => (
                      <Box key={field.key}>{renderField(field.key, field, template.id)}</Box>
                    ))}
                  </Stack>
                </Grid>

                {/* Right Column */}
                <Grid item xs={12} md={6}>
                  <Stack spacing={2} sx={{ marginTop: 0 }}>
                    {rightColumn.map((field) => (
                      <Box key={field.key}>{renderField(field.key, field, template.id)}</Box>
                    ))}
                  </Stack>
                </Grid>
                
                {/* File upload fields - full width below columns */}
                {fileFields.length > 0 && (
                  <Grid item xs={12}>
                    <Stack spacing={2}>
                      {fileFields.map((field) => (
                        <Box key={field.key}>{renderField(field.key, field, template.id)}</Box>
                      ))}
                    </Stack>
                  </Grid>
                )}
              </Grid>
            </Box>
            
            {/* Save/Update Form button moved to bottom */}
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3, mb: 2, px: 3 }}>
              <LoadingButton
                variant="contained"
                color="primary"
                onClick={() => handleSaveForm(template.id)}
                loading={savingForms[template.id] || false}
                startIcon={
                  !savingForms[template.id] && (
                    isFormSaved(template.id) ? (
                      <Icon icon="eva:edit-fill" width={20} />
                    ) : (
                      <Icon icon="eva:save-fill" width={20} />
                    )
                  )
                }
              >
                {isFormSaved(template.id) ? 'Update Form' : 'Save Form'}
              </LoadingButton>
            </Stack>
          </Box>
        )
      })}
    </Box>
  )
}

